import React from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { SITE } from "@/data/content";
import Logo from "./Logo";
import { Link } from "react-router-dom";
import { useAdminContent } from "@/lib/adminContent";
import { openCookieSettings } from "@/lib/cookieConsent";

const Footer = () => {
  const { t, dict, lang } = useI18n();
  const { contacts } = useAdminContent(lang);
  const year = Number(new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Bucharest", year: "numeric" }).format(new Date()));
  const items = ["home", "about", "gallery", "pricing", "faq", "contact"];

  return (
    <footer className="relative pt-16 sm:pt-20 pb-8 sm:pb-10 border-t border-[var(--border)] mt-6 sm:mt-10" data-testid="site-footer">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--accent)]/40 to-transparent" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 sm:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6 }}
            className="md:col-span-5"
          >
            <a
              href="#home"
              className="mb-4 sm:mb-5 inline-flex"
              aria-label="MAX Fitness"
              data-testid="footer-logo"
            >
              <Logo className="h-8 sm:h-10 w-auto" />
            </a>
            <p className="text-[var(--text-dim)] max-w-md leading-relaxed text-sm">
              {t("footer.description")}
            </p>
            <div className="mt-5 sm:mt-6 flex gap-2.5">
              <a href={contacts.social.facebook} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full glass flex items-center justify-center hover:border-[var(--accent)]/40 transition" aria-label="Facebook" data-testid="footer-facebook">
                <Facebook size={15} />
              </a>
              <a href={contacts.social.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full glass flex items-center justify-center hover:border-[var(--accent)]/40 transition" aria-label="Instagram" data-testid="footer-instagram">
                <Instagram size={15} />
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-3"
          >
            <h4 className="eyebrow mb-4 sm:mb-5">{t("footer.linksTitle")}</h4>
            <ul className="space-y-3">
              {items.map((key) => (
                <li key={key}>
                  <a href={`#${key === "home" ? "home" : key}`} className="text-sm text-[var(--text-dim)] hover:text-[var(--text)] transition" data-testid={`footer-link-${key}`}>
                    {t(`nav.${key}`)}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="md:col-span-4"
          >
            <h4 className="eyebrow mb-4 sm:mb-5">{t("footer.contactTitle")}</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <a href={`tel:${contacts.phone.replace(/\s/g, "")}`} className="flex items-start gap-2.5 hover:text-[var(--text)] transition">
                  <Phone size={14} className="text-[var(--accent)] mt-0.5" />
                  <span className="tabular-nums">{contacts.phone}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${contacts.email}`} className="flex items-start gap-2.5 hover:text-[var(--text)] transition">
                  <Mail size={14} className="text-[var(--accent)] mt-0.5" />
                  <span>{contacts.email}</span>
                </a>
              </li>
              <li className="flex items-start gap-2.5 text-[var(--text-dim)] leading-relaxed">
                <MapPin size={14} className="text-[var(--accent)] mt-0.5 flex-shrink-0" />
                <span>{contacts.address}</span>
              </li>
            </ul>
          </motion.div>
        </div>

        <div className="mt-10 sm:mt-14 pt-6 sm:pt-7 border-t border-[var(--border)] flex flex-col md:flex-row items-start md:items-center justify-between gap-3 sm:gap-4 text-xs text-[var(--text-mute)]">
          <div data-testid="footer-copyright">{dict.footer.copyright(year)}</div>
          <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
            <Link to="/privacy-policy" className="hover:text-[var(--text)] transition" data-testid="footer-privacy">
              {t("footer.privacy")}
            </Link>
            <Link to="/terms" className="hover:text-[var(--text)] transition" data-testid="footer-terms">
              {t("footer.terms")}
            </Link>
            <Link to="/cookies-policy" className="hover:text-[var(--text)] transition" data-testid="footer-cookies">
              {t("footer.cookies")}
            </Link>
            <button
              type="button"
              className="hover:text-[var(--text)] transition"
              onClick={openCookieSettings}
              data-testid="footer-cookie-settings"
            >
              Cookie settings
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
